import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaArrowRight, FaChartLine, FaShieldAlt, FaGift, FaRupeeSign, FaCalendarAlt, FaPercent } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const InvestmentPlans = () => {
  const [hoveredPlan, setHoveredPlan] = useState(null);

  // ============ 3 PLANS: Basic, Standard, Premium ============
  const plans = [
    {
      id: 'basic',
      name: 'Basic Plan',
      minAmount: 10000,
      maxAmount: 999999,
      returns: 60,
      duration: '12 months',
      features: ['Guaranteed 60% returns', 'Monthly profit payout', 'Zero hidden charges', 'Full capital protection'],
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      popular: false
    },
    {
      id: 'standard',
      name: 'Standard Plan',
      minAmount: 1000000,
      maxAmount: 1999999,
      returns: 66,
      duration: '12 months',
      features: ['Guaranteed 66% returns', 'Monthly profit payout', 'Zero hidden charges', 'Full capital protection', 'Priority support', 'Free Farm Visit'],
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium Plan',
      minAmount: 2000000,
      maxAmount: null,
      returns: 72,
      duration: '24 months',
      features: ['Guaranteed 72% returns', 'Monthly profit payout', 'Zero hidden charges', 'Full capital protection', 'Priority support', 'Free Farm Visit', 'Annual Bonus', 'Dedicated Manager'],
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      popular: false
    }
  ];

  return (
    <>
      <Helmet>
        <title>Investment Plans - Assets Dairy Farm</title>
      </Helmet>

      <div className="bg-gradient-to-br from-green-50 via-white to-blue-50 min-h-screen py-12">
        <div className="container mx-auto px-4">
          {/* Header with Badge */}
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full px-6 py-2 mb-4 shadow-md">
              <span className="font-semibold">📈 Up to 72% Guaranteed Returns • Low Risk</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Investment Plans</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Invest in <span className="text-green-600 font-bold">Assets Dairy Farm</span> and get guaranteed returns up to 72% annually
            </p>
            <div className="w-24 h-1 bg-green-500 mx-auto mt-4 rounded-full"></div>
          </div>

          {/* Features Highlight */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white text-center shadow-lg transform hover:scale-105 transition duration-300">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaChartLine className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-2">Up to 72% Returns</h3>
              <p className="text-green-100">Highest returns in the market</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white text-center shadow-lg transform hover:scale-105 transition duration-300">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaShieldAlt className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-2">100% Capital Protection</h3>
              <p className="text-blue-100">Your principal amount is fully protected</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white text-center shadow-lg transform hover:scale-105 transition duration-300">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaGift className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-2">Monthly Payouts</h3>
              <p className="text-purple-100">Get profits credited to your wallet monthly</p>
            </div>
          </div>

          {/* Plans Grid - 3 Plans */}
          <div className="flex justify-center mb-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl w-full">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 transform ${hoveredPlan === plan.id ? 'scale-105 shadow-2xl' : 'hover:shadow-xl'}`}
                  onMouseEnter={() => setHoveredPlan(plan.id)}
                  onMouseLeave={() => setHoveredPlan(null)}
                >
                  {plan.popular && (
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-center py-2 text-sm font-bold text-white">
                      🔥 Most Popular Choice 🔥
                    </div>
                  )}
                  <div className={`bg-gradient-to-r ${plan.color} p-6 text-white text-center`}>
                    <h3 className="text-2xl font-bold">{plan.name}</h3>
                    <p className="text-5xl font-bold mt-3">{plan.returns}%</p>
                    <p className="text-sm opacity-90 mt-1">Guaranteed Returns Annually</p>
                  </div>
                  <div className="p-6">
                    <div className="text-center mb-4">
                      <p className="text-gray-500 text-sm">Minimum Investment</p>
                      <p className="text-2xl font-bold text-gray-800">₹{plan.minAmount.toLocaleString()}</p>
                      {plan.maxAmount && (
                        <>
                          <p className="text-gray-500 text-sm mt-2">Maximum Investment</p>
                          <p className="text-lg font-semibold text-gray-700">₹{plan.maxAmount.toLocaleString()}</p>
                        </>
                      )}
                      {!plan.maxAmount && plan.id === 'premium' && (
                        <p className="text-sm text-green-600 mt-2">✨ Unlimited Investment Available ✨</p>
                      )}
                    </div>

                    <div className="border-t border-gray-100 my-4"></div>

                    <div className="space-y-2 mb-6">
                      {plan.features.map((feature, idx) => (
                        <p key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                          <FaCheckCircle className="text-green-500 text-xs" /> {feature}
                        </p>
                      ))}
                    </div>

                    <Link
                      to="/investor/payment"
                      state={{ selectedPlan: plan }}
                      className={`block text-center py-3 rounded-lg font-semibold transition bg-gradient-to-r ${plan.color} text-white hover:opacity-90 text-lg shadow-md`}
                    >
                      Invest Now <FaArrowRight className="inline ml-2" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Calculator Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">📊 Returns Calculator</h2>
              <p className="text-gray-500">Calculate your potential earnings</p>
              <div className="w-20 h-1 bg-green-500 mx-auto mt-2 rounded-full"></div>
            </div>
            <Calculator />
          </div>

          {/* Farm Info Banner */}
          <div className="mt-12 bg-gradient-to-r from-green-700 to-green-900 rounded-2xl p-8 text-white text-center shadow-xl">
            <div className="mb-4">
              <span className="text-5xl">🏆</span>
            </div>
            <h2 className="text-2xl font-bold mb-3">Assets Dairy Farm</h2>
            <p className="text-lg mb-2 opacity-95">India's Most Trusted Dairy Farm Investment Platform</p>
            <p className="text-sm opacity-80">Invest in our farm and earn guaranteed returns up to 72% annually</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <div className="bg-white/20 rounded-full px-4 py-1.5 text-sm">✓ 12,500+ Happy Investors</div>
              <div className="bg-white/20 rounded-full px-4 py-1.5 text-sm">✓ ₹250Cr+ Assets Managed</div>
              <div className="bg-white/20 rounded-full px-4 py-1.5 text-sm">✓ 15+ Partner Farms</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Calculator Component - Dynamic Rate based on plan selection
const Calculator = () => {
  const [amount, setAmount] = useState(10000);
  const [plan, setPlan] = useState('basic');
  const [years, setYears] = useState(1);

  const plans = {
    basic: { rate: 60, name: 'Basic', min: 10000 },
    standard: { rate: 66, name: 'Standard', min: 1000000 },
    premium: { rate: 72, name: 'Premium', min: 2000000 }
  };

  const selectedPlan = plans[plan];
  const rate = selectedPlan.rate;

  const totalReturns = amount * (rate / 100) * years;
  const monthlyReturns = totalReturns / (years * 12);
  const maturityAmount = amount + totalReturns;

  return (
    <div className="space-y-6">
      {/* Plan Selection in Calculator */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Plan</label>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(plans).map(([key, p]) => (
            <button
              key={key}
              onClick={() => {
                setPlan(key);
                if (amount < p.min) setAmount(p.min);
              }}
              className={`py-2 px-3 rounded-lg text-sm font-medium transition ${plan === key
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              {p.name}<br />
              <span className="text-xs">{p.rate}%</span>
            </button>
          ))}
        </div>
      </div>

      {/* Amount Slider */}
      <div>
        <div className="flex justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Investment Amount</label>
          <span className="text-lg font-bold text-green-600">₹{amount.toLocaleString()}</span>
        </div>
        <input
          type="range"
          min={selectedPlan.min}
          max="50000000"
          step="10000"
          value={amount}
          onChange={(e) => setAmount(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>₹{selectedPlan.min.toLocaleString()}</span>
          <span>₹5Cr+</span>
        </div>
      </div>

      {/* Duration Selector */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-600 text-sm">Interest Rate</p>
            <p className="text-2xl font-bold text-green-600">{rate}% p.a.</p>
          </div>
          <div className="text-right">
            <p className="text-gray-600 text-sm">Duration</p>
            <div className="flex gap-2 mt-1">
              {[1, 2, 3].map((y) => (
                <button
                  key={y}
                  onClick={() => setYears(y)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition ${years === y
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {y}Y
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-5 text-white">
        <p className="text-gray-400 text-sm mb-3">📈 Your Returns Summary</p>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-300">Total Returns ({years} Year{years > 1 ? 's' : ''}):</span>
            <span className="font-bold text-green-400">₹{totalReturns.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-300">Monthly Returns:</span>
            <span className="font-bold text-green-400">₹{monthlyReturns.toFixed(0).toLocaleString()}</span>
          </div>
          <div className="border-t border-gray-700 my-2 pt-2 flex justify-between">
            <span className="font-semibold">💰 Maturity Amount:</span>
            <span className="font-bold text-yellow-400 text-lg">₹{maturityAmount.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentPlans;